import React, { useState, useEffect } from "react";
import { useThumbnailUpload } from "../../../hooks/useThumbnailUpload";
import { useTemplates } from "../../../hooks/useTemplates";
import { useMaterialManager } from "../../../hooks/useMaterialManager";
import { MaterialForm, Group } from "../types";

interface MateriaisTabProps {
  // Props para modais e funcionalidades compartilhadas
  onShowContentSelectionModal: (show: boolean) => void;
  onPreviewContent: (content: any) => void;
  selectedContentIds: string[];
  setSelectedContentIds: (ids: string[]) => void;
  contents: any[];
  materials: any[]; // Receber materiais do ManagerPage
  onManageMaterial?: (material: any) => void;
  onDeleteMaterial?: (materialId: string) => void;
  onMaterialCreated?: () => void; // Callback para atualizar lista após criar material
}

const MateriaisTab: React.FC<MateriaisTabProps> = ({
  onShowContentSelectionModal,
  onPreviewContent,
  selectedContentIds,
  setSelectedContentIds,
  contents,
  materials,
  onManageMaterial,
  onDeleteMaterial,
  onMaterialCreated,
}) => {
  // Estados locais para a aba de materiais
  const [materialForm, setMaterialForm] = useState<MaterialForm>({
    title: "",
    description: "",
    keywords: [] as string[],
    thumbnailKey: "",
    introductionUrl: "",
    purchaseUrl: "",
    referenceIds: [],
    isFree: false,
    templateId: "",
    groups: []
  });
  const [groups, setGroups] = useState<Group[]>([]);
  const [newGroupName, setNewGroupName] = useState("");
  const [activeGroupIndex, setActiveGroupIndex] = useState<number | null>(null);

  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [hasPreview, setHasPreview] = useState<boolean>(false);
  const [currentMaterialPage, setCurrentMaterialPage] = useState(1);
  const materialPageSize = 10;
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [keywordInput, setKeywordInput] = useState<string>("");
  const [referenceIdInput, setReferenceIdInput] = useState<string>("");
  const [localMaterials, setLocalMaterials] = useState<any[]>([]);
  const [hasOrderChanged, setHasOrderChanged] = useState(false);

  // Hooks específicos para materiais
  const { createMaterialWithThumbnail, uploadState, resetUploadState } =
    useThumbnailUpload();
  const { templates, fetchTemplates } = useTemplates();
  const { reorderMaterials } = useMaterialManager();

  // Buscar templates ao montar o componente
  useEffect(() => {
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Effect to handle incoming selected contents and assign to active group
  useEffect(() => {
    if (selectedContentIds.length > 0 && activeGroupIndex !== null) {
      setGroups(prev => {
        const newGroups = [...prev];
        if (newGroups[activeGroupIndex]) {
          const group = newGroups[activeGroupIndex];
          // Add unique IDs
          const newIds = selectedContentIds.filter(id => !group.contentIds.includes(id));
          group.contentIds = [...group.contentIds, ...newIds];
        }
        return newGroups;
      });
      // Clear the selection from parent
      setSelectedContentIds([]);
      setActiveGroupIndex(null);
    }
  }, [selectedContentIds, activeGroupIndex, setSelectedContentIds]);

  // Sincronizar e ordenar materiais locais quando a prop materials mudar
  useEffect(() => {
    if (materials) {
      const sorted = [...materials].sort((a, b) => {
        const indexA = a.index !== undefined ? a.index : Number.MAX_SAFE_INTEGER;
        const indexB = b.index !== undefined ? b.index : Number.MAX_SAFE_INTEGER;
        return indexA - indexB;
      });
      setLocalMaterials(sorted);
      setHasOrderChanged(false);
    }
  }, [materials]);

  const moveMaterial = (index: number, direction: 'up' | 'down') => {
    const newMaterials = [...localMaterials];
    if (direction === 'up' && index > 0) {
      [newMaterials[index], newMaterials[index - 1]] = [newMaterials[index - 1], newMaterials[index]];
    } else if (direction === 'down' && index < newMaterials.length - 1) {
      [newMaterials[index], newMaterials[index + 1]] = [newMaterials[index + 1], newMaterials[index]];
    }
    setLocalMaterials(newMaterials);
    setHasOrderChanged(true);
  };

  const handleSaveOrder = async () => {
    const orderedIds = localMaterials.map((material, index) => ({
      materialId: material.id,
      index: index
    }));
    
    try {
      await reorderMaterials(orderedIds);
      setHasOrderChanged(false);
      alert('Ordem salva com sucesso!');
      if (onMaterialCreated) onMaterialCreated(); // Recarregar lista
    } catch (error) {
      alert('Erro ao salvar ordem.');
    }
  };

  // Funções específicas para materiais
  const handleMaterialFormChange = (
    field: string,
    value: string | string[] | boolean
  ) => {
    setMaterialForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleKeywordInputKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Enter" && keywordInput.trim()) {
      e.preventDefault();
      const newKeyword = keywordInput.trim();
      // Verificar se a keyword já existe (case-insensitive)
      if (
        !materialForm.keywords.some(
          (k) => k.toLowerCase() === newKeyword.toLowerCase()
        )
      ) {
        setMaterialForm((prev) => ({
          ...prev,
          keywords: [...prev.keywords, newKeyword],
        }));
      }
      setKeywordInput("");
    }
  };

  const handleRemoveKeyword = (keywordToRemove: string) => {
    setMaterialForm((prev) => ({
      ...prev,
      keywords: prev.keywords.filter((k) => k !== keywordToRemove),
    }));
  };

  const handleReferenceIdInputKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Enter" && referenceIdInput.trim()) {
      e.preventDefault();
      const newRefId = referenceIdInput.trim();
      if (!materialForm.referenceIds.includes(newRefId)) {
        setMaterialForm((prev) => ({
          ...prev,
          referenceIds: [...prev.referenceIds, newRefId],
        }));
      }
      setReferenceIdInput("");
    }
  };

  const handleRemoveReferenceId = (refIdToRemove: string) => {
    setMaterialForm((prev) => ({
      ...prev,
      referenceIds: prev.referenceIds.filter((id) => id !== refIdToRemove),
    }));
  };

  const handleThumbnailSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setThumbnailFile(file);
    }
  };

  const handlePreviewFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPreviewFile(file);
    }
  };

  const handleAddGroup = () => {
    if (!newGroupName.trim()) return;
    setGroups([...groups, { name: newGroupName, contentIds: [] }]);
    setNewGroupName("");
  };

  const handleRemoveGroup = (index: number) => {
    const newGroups = [...groups];
    newGroups.splice(index, 1);
    setGroups(newGroups);
  };

  const handleAddContentToGroup = (index: number) => {
    setActiveGroupIndex(index);
    onShowContentSelectionModal(true);
  };

  const handleRemoveContentFromGroup = (groupIndex: number, contentId: string) => {
    setGroups(prev => {
      const newGroups = [...prev];
      const group = newGroups[groupIndex];
      group.contentIds = group.contentIds.filter(id => id !== contentId);
      return newGroups;
    });
  };

  const handleMoveContentInGroup = (groupIndex: number, contentIndex: number, direction: 'up' | 'down') => {
    setGroups(prev => {
      const newGroups = [...prev];
      // Create a copy of the group to avoid mutating state directly
      const group = { ...newGroups[groupIndex] };
      const newContentIds = [...group.contentIds];
      
      if (direction === 'up' && contentIndex > 0) {
        [newContentIds[contentIndex], newContentIds[contentIndex - 1]] = [newContentIds[contentIndex - 1], newContentIds[contentIndex]];
      } else if (direction === 'down' && contentIndex < newContentIds.length - 1) {
        [newContentIds[contentIndex], newContentIds[contentIndex + 1]] = [newContentIds[contentIndex + 1], newContentIds[contentIndex]];
      }
      
      group.contentIds = newContentIds;
      newGroups[groupIndex] = group;
      return newGroups;
    });
  };

  const handleCreateMaterial = async () => {
    if (!materialForm.title || materialForm.referenceIds.length === 0) {
      alert("Por favor, preencha o título e os IDs de referência.");
      return;
    }

    if (!thumbnailFile) {
      alert("Por favor, selecione uma thumbnail para o material.");
      return;
    }

    if (groups.length === 0) {
      alert("Por favor, crie pelo menos um grupo de conteúdos.");
      return;
    }

    const hasEmptyGroups = groups.some(g => g.contentIds.length === 0);
    if (hasEmptyGroups) {
      alert("Todos os grupos devem ter pelo menos um conteúdo.");
      return;
    }

    const previewValue = hasPreview && previewFile !== null;

    const materialData = {
      title: materialForm.title,
      description: materialForm.description,
      keywords: materialForm.keywords,
      introductionUrl: materialForm.introductionUrl,
      purchaseUrl: materialForm.purchaseUrl,
      referenceIds: materialForm.referenceIds,
      isFree: materialForm.isFree,
      templateId: materialForm.templateId.trim() || undefined,
      contentIds: [], // Legacy support or empty
      groups: groups,
      preview: previewValue, // Flag: só tem preview se marcado E tiver arquivo
    };

    try {
      const result = await createMaterialWithThumbnail(
        materialData,
        thumbnailFile,
        previewFile
      );

      if (result) {
        // Reset form
        setMaterialForm({
          title: "",
          description: "",
          keywords: [],
          thumbnailKey: "",
          introductionUrl: "",
          purchaseUrl: "",
          referenceIds: [],
          isFree: false,
          templateId: "",
          groups: []
        });
        setGroups([]);
        setThumbnailFile(null);
        setPreviewFile(null);
        setHasPreview(false);
        setKeywordInput("");
        setReferenceIdInput("");
        setSelectedContentIds([]);

        // Resetar estado de upload para permitir criar outro material
        resetUploadState();

        // Atualizar lista de materiais
        if (onMaterialCreated) {
          onMaterialCreated();
        }

        // Mostrar modal de sucesso
        setShowSuccessModal(true);
      }
    } catch (error) {
      alert("Erro ao criar material. Tente novamente.");
    }
  };

  const handleRemoveContent = (contentId: string) => {
    // Deprecated in favor of handleRemoveContentFromGroup
    setSelectedContentIds(selectedContentIds.filter((id) => id !== contentId));
  };

  // Paginação de materiais
  const totalMaterialPages = Math.ceil(localMaterials.length / materialPageSize);
  const getPaginatedMaterials = () => {
    const startIndex = (currentMaterialPage - 1) * materialPageSize;
    const endIndex = startIndex + materialPageSize;
    return localMaterials.slice(startIndex, endIndex);
  };

  // Loading state - removido pois agora usa materiais do ManagerPage

  // Error state - removido pois agora usa materiais do ManagerPage

  return (
    <div className="materiais-tab">
      <h2>Criar Novo Material</h2>
      <p>Configure um material com múltiplos conteúdos</p>

      <div className="material-form">
        <div className="form-group">
          <label>Título do Material:</label>
          <input
            type="text"
            value={materialForm.title}
            onChange={(e) => handleMaterialFormChange("title", e.target.value)}
            placeholder="Ex: Matemática Básica"
          />
        </div>

        <div className="form-group">
          <label>Descrição:</label>
          <textarea
            value={materialForm.description}
            onChange={(e) =>
              handleMaterialFormChange("description", e.target.value)
            }
            placeholder="Descreva o material..."
            rows={3}
          />
        </div>

        <div className="form-group">
          <label>Palavras-chave:</label>
          <div className="keywords-input-container">
            {materialForm.keywords.length > 0 && (
              <div className="keywords-tags">
                {materialForm.keywords.map((keyword, index) => (
                  <span key={index} className="keyword-tag">
                    {keyword}
                    <button
                      type="button"
                      className="keyword-remove-btn"
                      onClick={() => handleRemoveKeyword(keyword)}
                      title="Remover palavra-chave"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
            <input
              type="text"
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              onKeyDown={handleKeywordInputKeyDown}
              placeholder="Digite uma palavra e pressione Enter"
              className="keyword-input"
            />
          </div>
          <small className="form-help">
            💡 Digite uma palavra e pressione Enter para adicionar
          </small>
        </div>

        <div className="form-group">
          <label>Thumbnail (Imagem de Capa) *:</label>
          <input
            type="file"
            accept="image/*"
            onChange={handleThumbnailSelect}
            className="thumbnail-upload-input"
            required
          />
          {thumbnailFile && (
            <div className="thumbnail-preview">
              <img
                src={URL.createObjectURL(thumbnailFile)}
                alt="Preview da thumbnail"
                className="thumbnail-preview-image"
              />
              <span className="thumbnail-file-name">{thumbnailFile.name}</span>
              {uploadState.isUploading && (
                <div className="upload-progress">
                  <span>Fazendo upload... {uploadState.progress}%</span>
                </div>
              )}
            </div>
          )}
          {hasPreview && (
            <small className="form-help">
              📸 Se não houver preview customizado, será usado o primeiro
              conteúdo como preview
            </small>
          )}
        </div>

        <div className="form-group">
          <label>
            <input
              type="checkbox"
              checked={hasPreview}
              onChange={(e) => {
                setHasPreview(e.target.checked);
                if (!e.target.checked) {
                  setPreviewFile(null);
                }
              }}
              style={{ marginRight: "8px" }}
            />
            Material terá preview customizado
          </label>
          {hasPreview && (
            <>
              <input
                type="file"
                accept="image/*,application/pdf"
                onChange={handlePreviewFileSelect}
                className="preview-upload-input"
                style={{ marginTop: "8px" }}
              />
            </>
          )}
          {previewFile && (
            <div className="thumbnail-preview">
              {previewFile.type === "application/pdf" ? (
                <div style={{ padding: "10px", textAlign: "center" }}>
                  <span style={{ fontSize: "48px" }}>📄</span>
                  <div style={{ marginTop: "8px" }}>
                    <span className="thumbnail-file-name">
                      {previewFile.name}
                    </span>
                  </div>
                  <small
                    style={{
                      display: "block",
                      marginTop: "4px",
                      color: "#999",
                    }}
                  >
                    PDF selecionado
                  </small>
                </div>
              ) : (
                <>
                  <img
                    src={URL.createObjectURL(previewFile)}
                    alt="Preview"
                    className="thumbnail-preview-image"
                  />
                  <span className="thumbnail-file-name">
                    {previewFile.name}
                  </span>
                </>
              )}
            </div>
          )}
          <small className="form-help">
            🖼️ Imagem ou PDF para preview do material
          </small>
        </div>

        <div className="form-group">
          <label>URL de Introdução (Vídeo):</label>
          <input
            type="url"
            value={materialForm.introductionUrl}
            onChange={(e) =>
              handleMaterialFormChange("introductionUrl", e.target.value)
            }
            placeholder="https://youtube.com/watch?v=..."
          />
          <small className="form-help">
            🎥 Link do vídeo de apresentação (YouTube, Vimeo, etc)
          </small>
        </div>

        <div className="form-group">
          <label>URL de Compra:</label>
          <input
            type="url"
            value={materialForm.purchaseUrl}
            onChange={(e) =>
              handleMaterialFormChange("purchaseUrl", e.target.value)
            }
            placeholder="https://exemplo.com/comprar"
          />
        </div>

        <div className="form-group">
          <label>IDs de Referência *:</label>
          <div className="keywords-input-container">
            {materialForm.referenceIds.length > 0 && (
              <div className="keywords-tags">
                {materialForm.referenceIds.map((refId, index) => (
                  <span key={index} className="keyword-tag">
                    {refId}
                    <button
                      type="button"
                      className="keyword-remove-btn"
                      onClick={() => handleRemoveReferenceId(refId)}
                      title="Remover ID"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
            <input
              type="text"
              value={referenceIdInput}
              onChange={(e) => setReferenceIdInput(e.target.value)}
              onKeyDown={handleReferenceIdInputKeyDown}
              placeholder="Digite um ID e pressione Enter"
              className="keyword-input"
            />
          </div>
          <small className="form-help">
            🆔 IDs do produto na plataforma de pagamento (Hotmart, Eduzz, etc) -
            Campo obrigatório. Pressione Enter para adicionar.
          </small>
        </div>

        <div className="form-group">
          <div
            className="material-free-checkbox"
            style={{
              backgroundColor: materialForm.isFree ? "#e8f5e9" : "#fff3e0",
              padding: "16px",
              borderRadius: "8px",
              border: `2px solid ${
                materialForm.isFree ? "#4caf50" : "#ff9800"
              }`,
              marginBottom: "16px",
            }}
          >
            <label
              className="checkbox-label"
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "12px",
                cursor: "pointer",
                margin: 0,
              }}
            >
              <input
                type="checkbox"
                checked={materialForm.isFree}
                onChange={(e) =>
                  handleMaterialFormChange("isFree", e.target.checked)
                }
                style={{
                  marginTop: "4px",
                  width: "20px",
                  height: "20px",
                  cursor: "pointer",
                }}
              />
              <div>
                <div
                  style={{
                    fontSize: "16px",
                    fontWeight: "bold",
                    marginBottom: "4px",
                  }}
                >
                  🎁 Material Gratuito
                </div>
                <small style={{ color: "#666", display: "block" }}>
                  Marque se o material for de acesso livre para todos os
                  usuários
                </small>
              </div>
            </label>
          </div>
        </div>

        <div className="form-group">
          <label>Template (opcional):</label>
          <select
            value={materialForm.templateId}
            onChange={(e) =>
              handleMaterialFormChange("templateId", e.target.value)
            }
            style={{
              width: "100%",
              padding: "10px",
              fontSize: "16px",
              border: "1px solid #444",
              borderRadius: "4px",
              backgroundColor: "#2B2B2B",
              color: "#fff",
              cursor: "pointer",
            }}
          >
            <option value="">Nenhum template</option>
            {templates.map((template) => (
              <option key={template.id} value={template.id}>
                {template.name}
              </option>
            ))}
          </select>
          <small className="form-help">
            💡 Selecione um template HTML para o material (opcional)
          </small>
        </div>

        <div className="content-selection-section">
          <div className="content-selection-info">
            <h4>Grupos de Conteúdo</h4>
            <p>Organize os conteúdos do material em grupos</p>
          </div>

          <div className="add-group-container" style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
            <input
              type="text"
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              placeholder="Nome do novo grupo"
              style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid #444', backgroundColor: '#333', color: 'white' }}
            />
            <button
              onClick={handleAddGroup}
              disabled={!newGroupName.trim()}
              style={{
                padding: '8px 16px',
                backgroundColor: '#4caf50',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: newGroupName.trim() ? 'pointer' : 'not-allowed',
                opacity: newGroupName.trim() ? 1 : 0.6
              }}
            >
              + Adicionar Grupo
            </button>
          </div>

          {groups.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px', color: '#999', border: '1px dashed #444', borderRadius: '8px' }}>
              Nenhum grupo criado. Adicione um grupo para começar a inserir conteúdos.
            </div>
          ) : (
            <div className="groups-list" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {groups.map((group, groupIndex) => (
                <div key={groupIndex} className="group-card" style={{ border: '1px solid #444', borderRadius: '8px', padding: '16px', backgroundColor: '#2B2B2B' }}>
                  <div className="group-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', borderBottom: '1px solid #444', paddingBottom: '8px' }}>
                    <h5 style={{ margin: 0, fontSize: '18px' }}>{group.name}</h5>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button
                        onClick={() => handleAddContentToGroup(groupIndex)}
                        style={{
                          padding: '4px 12px',
                          backgroundColor: '#2196f3',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '14px'
                        }}
                      >
                        + Conteúdo
                      </button>
                      <button
                        onClick={() => handleRemoveGroup(groupIndex)}
                        style={{
                          padding: '4px 12px',
                          backgroundColor: '#f44336',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '14px'
                        }}
                      >
                        Remover Grupo
                      </button>
                    </div>
                  </div>

                  {group.contentIds.length === 0 ? (
                    <p style={{ color: '#999', fontStyle: 'italic', margin: 0 }}>Nenhum conteúdo neste grupo.</p>
                  ) : (
                    <div className="selected-contents-table-container">
                      <table className="selected-contents-table">
                        <thead>
                          <tr>
                            <th style={{ width: '40px' }}>#</th>
                            <th>Nome</th>
                            <th>Tipo</th>
                            <th>Ações</th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.contentIds.map((contentId, contentIndex) => {
                            const content = contents.find(c => c.id === contentId);
                            if (!content) return null;

                            return (
                              <tr key={`${content.id}-${contentIndex}`} className="selected-content-row">
                                <td className="content-order-cell">
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                    <button 
                                      type="button"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleMoveContentInGroup(groupIndex, contentIndex, 'up');
                                      }}
                                      disabled={contentIndex === 0}
                                      style={{ 
                                        cursor: contentIndex === 0 ? 'default' : 'pointer', 
                                        opacity: contentIndex === 0 ? 0.3 : 1, 
                                        border: 'none', 
                                        background: 'none', 
                                        color: 'white',
                                        fontSize: '10px',
                                        padding: 0
                                      }}
                                    >
                                      ▲
                                    </button>
                                    <button 
                                      type="button"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleMoveContentInGroup(groupIndex, contentIndex, 'down');
                                      }}
                                      disabled={contentIndex === group.contentIds.length - 1}
                                      style={{ 
                                        cursor: contentIndex === group.contentIds.length - 1 ? 'default' : 'pointer', 
                                        opacity: contentIndex === group.contentIds.length - 1 ? 0.3 : 1, 
                                        border: 'none', 
                                        background: 'none', 
                                        color: 'white',
                                        fontSize: '10px',
                                        padding: 0
                                      }}
                                    >
                                      ▼
                                    </button>
                                  </div>
                                </td>
                                <td className="content-name-cell">
                                  <span className="content-name">{content.name}</span>
                                </td>
                                <td className="content-type-cell">
                                  <span className="content-type-badge">{content.type}</span>
                                </td>
                                <td className="content-actions-cell">
                                  <div className="action-buttons">
                                    <button
                                      className="preview-content-btn"
                                      onClick={() => onPreviewContent(content)}
                                      title="Visualizar conteúdo"
                                    >
                                      👁️
                                    </button>
                                    <button
                                      className="remove-content-btn"
                                      onClick={() => handleRemoveContentFromGroup(groupIndex, content.id)}
                                      title="Remover do grupo"
                                    >
                                      ❌
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="form-actions">
          <button
            className="create-material-button"
            onClick={handleCreateMaterial}
            disabled={
              !materialForm.title ||
              materialForm.referenceIds.length === 0 ||
              !thumbnailFile ||
              groups.length === 0 ||
              uploadState.isUploading
            }
          >
            {uploadState.isUploading ? "Criando Material..." : "Criar Material"}
          </button>
        </div>

        {uploadState.error && (
          <div className="upload-error">❌ {uploadState.error}</div>
        )}
      </div>

      {/* Lista de Materiais */}
      <div className="materials-list-section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3>📋 Meus Materiais</h3>
          {hasOrderChanged && (
            <button 
              onClick={handleSaveOrder}
              className="save-order-btn"
              style={{
                backgroundColor: '#4caf50',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              💾 Salvar Ordem
            </button>
          )}
        </div>

        {localMaterials.length === 0 ? (
          <p>Nenhum material encontrado.</p>
        ) : (
          <>
            <div className="materials-table-container">
              <table className="materials-table">
                <thead>
                  <tr>
                    <th style={{ width: '60px', textAlign: 'center' }}>Ordem</th>
                    <th>Nome</th>
                    <th>ID de Referência</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {getPaginatedMaterials().map((material, index) => {
                    const globalIndex = (currentMaterialPage - 1) * materialPageSize + index;
                    return (
                    <tr key={material.id} className="material-row">
                      <td className="material-order-cell" style={{ textAlign: 'center' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                          <button 
                            onClick={() => moveMaterial(globalIndex, 'up')}
                            disabled={globalIndex === 0}
                            title="Mover para cima"
                            style={{ 
                              cursor: globalIndex === 0 ? 'default' : 'pointer', 
                              opacity: globalIndex === 0 ? 0.3 : 1, 
                              border: 'none', 
                              background: 'rgba(255,255,255,0.1)', 
                              color: 'white',
                              borderRadius: '4px',
                              width: '24px',
                              height: '24px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '12px'
                            }}
                          >
                            ▲
                          </button>
                          <button 
                            onClick={() => moveMaterial(globalIndex, 'down')}
                            disabled={globalIndex === localMaterials.length - 1}
                            title="Mover para baixo"
                            style={{ 
                              cursor: globalIndex === localMaterials.length - 1 ? 'default' : 'pointer', 
                              opacity: globalIndex === localMaterials.length - 1 ? 0.3 : 1, 
                              border: 'none', 
                              background: 'rgba(255,255,255,0.1)', 
                              color: 'white',
                              borderRadius: '4px',
                              width: '24px',
                              height: '24px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '12px'
                            }}
                          >
                            ▼
                          </button>
                        </div>
                      </td>
                      <td className="material-name-cell">
                        <div className="material-name-wrapper">
                          <span className="material-name">
                            {material.title}
                          </span>
                          {material.description && (
                            <span className="material-description">
                              {material.description}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="material-reference-cell">
                        <span className="reference-id">
                          {material.referenceIds &&
                          material.referenceIds.length > 0
                            ? material.referenceIds.join(", ")
                            : material.referenceId || "N/A"}
                        </span>
                      </td>
                      <td className="material-actions-cell">
                        <div className="action-buttons">
                          {onManageMaterial && (
                            <button
                              className="manage-material-btn"
                              onClick={() => onManageMaterial(material)}
                              title="Gerenciar material"
                            >
                              ⚙️
                            </button>
                          )}
                          {onDeleteMaterial && (
                            <button
                              className="delete-material-btn"
                              onClick={() => onDeleteMaterial(material.id)}
                              title="Deletar material"
                            >
                              🗑️
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ); })}
                </tbody>
              </table>
            </div>

            {/* Paginação */}
            {totalMaterialPages > 1 && (
              <div className="pagination-controls">
                <div className="pagination-info">
                  Página {currentMaterialPage} de {totalMaterialPages} (
                  {localMaterials.length} materiais)
                </div>

                <div className="pagination-buttons">
                  {currentMaterialPage > 1 && (
                    <button
                      className="pagination-btn"
                      onClick={() =>
                        setCurrentMaterialPage(currentMaterialPage - 1)
                      }
                    >
                      ← Anterior
                    </button>
                  )}

                  {Array.from({ length: totalMaterialPages }, (_, i) => i + 1)
                    .filter((page) => {
                      return (
                        page === 1 ||
                        page === totalMaterialPages ||
                        Math.abs(page - currentMaterialPage) <= 2
                      );
                    })
                    .map((page, index, array) => (
                      <React.Fragment key={page}>
                        {index > 0 && array[index - 1] !== page - 1 && (
                          <span className="pagination-dots">...</span>
                        )}

                        <button
                          className={`pagination-btn ${
                            currentMaterialPage === page ? "active" : ""
                          }`}
                          onClick={() => setCurrentMaterialPage(page)}
                        >
                          {page}
                        </button>
                      </React.Fragment>
                    ))}

                  {currentMaterialPage < totalMaterialPages && (
                    <button
                      className="pagination-btn"
                      onClick={() =>
                        setCurrentMaterialPage(currentMaterialPage + 1)
                      }
                    >
                      Próxima →
                    </button>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal de Sucesso */}
      {showSuccessModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowSuccessModal(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div style={{ textAlign: "center", padding: "20px" }}>
              <div
                style={{
                  fontSize: "64px",
                  marginBottom: "20px",
                  animation: "bounce 0.6s ease-in-out",
                }}
              >
                ✅
              </div>
              <h2 style={{ color: "#4caf50", marginBottom: "16px" }}>
                Material criado com sucesso!
              </h2>
              <p style={{ color: "#666", marginBottom: "24px" }}>
                O material foi criado e já está disponível na lista.
              </p>
              <button
                onClick={() => setShowSuccessModal(false)}
                style={{
                  backgroundColor: "#4caf50",
                  color: "white",
                  border: "none",
                  padding: "12px 32px",
                  borderRadius: "8px",
                  fontSize: "16px",
                  fontWeight: "bold",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#45a049";
                  e.currentTarget.style.transform = "scale(1.05)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#4caf50";
                  e.currentTarget.style.transform = "scale(1)";
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes bounce {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-20px);
          }
        }
      `}</style>
    </div>
  );
};

export default MateriaisTab;
